import { useEffect, useMemo, useState } from 'react';
import useApi from '../../../../../utils/AxiosWrapper';
import { useUser } from '../../../../../utils/UserContext';
import { ClipLoader } from 'react-spinners';
import RevenueAndExpensesWidget from '../home/widgets/RevenueAndExpensesWidget';
import CostRevenuePerMileWidget from './widgets/overview/CostRevenuePerMileWidget';
import BudgetVsActualExpensesWidget from './widgets/overview/BudgetVsActualExpensesWidget';
import ChartFiltersWidget from './widgets/overview/ChartFiltersWidget';
import {
  BudgetedVsTargetByCategoryData,
  CostRevenuePerMileData,
  Entity,
  RevenueExpensesData,
  TruckRevenueExpenseData,
  CustomerWithDriverOnboarding,
} from '../../../../../types/apiTypes';
import { useScreenView } from '../../../../../utils/ScreenViewContext';
import { useQuery } from '@tanstack/react-query';
import RevenuevsExpensesStackedBars from './widgets/overview/RevenuevsExpensesStackedBars';
import { Truck } from '../../../../../types/dbTypes';
import {
  isPreActiveCustomer,
  getDemoTrucks,
  getDemoEarliestDate,
  getDemoCpmRpmData,
  getDemoTotalMilesDriven,
  getDemoRevenueExpensesData,
  getDemoTrucksRevenueExpenseData,
  getDemoBudgetedVsTargetData,
} from '../home/utils/demoData';
import { DemoDataAlert } from '../home/components/DemoDataAlert';

interface FinancialOverviewProps {
  userType: string;
  currentPage: string;
  currentTab: string;
  setCurrentTab: (tab: string) => void;
  subTab: string;
  setSubTab: (tab: string) => void;
}

function FinancialOverview({
  userType,
  currentPage,
  currentTab,
  setCurrentTab,
  subTab,
  setSubTab,
}: FinancialOverviewProps) {
  const { screenView } = useScreenView();
  const { callProtectedApi } = useApi();
  const { userRecord, userId } = useUser();
  const [selectedTruck, setSelectedTruck] = useState<string>('all');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [startDate, setStartDate] = useState<Date>();
  const [endDate, setEndDate] = useState<Date>(new Date());

  // ============================================================================
  // Pre-Active Customer Detection
  // ============================================================================

  const customerStatus = (userRecord as CustomerWithDriverOnboarding)?.status;
  const isPreActive = useMemo(
    () => isPreActiveCustomer(customerStatus),
    [customerStatus]
  );

  // Only fetch real data if customer is active
  const shouldFetchRealData = !!userId && !isPreActive;

  // Fetch earliest transaction date first
  const fetchEarliestTransactionDate = async (customer_id: string) => {
    const response = await callProtectedApi(
      `/charts/earliest-transaction-date/${customer_id}`,
      'get',
      {},
      'Error occurred while fetching earliest transaction date.'
    );
    return response?.data?.data?.start_date;
  };

  const { data: earliestDate, isLoading: loadingEarliestDate } = useQuery({
    queryKey: ['earliestTransactionDate', userId],
    queryFn: () => fetchEarliestTransactionDate(userId ?? ''),
    enabled: shouldFetchRealData,
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 30 * 60 * 1000,
  });

  // Set the start date when earliest date is fetched
  useEffect(() => {
    if (earliestDate && !startDate) {
      setStartDate(new Date(earliestDate));
    }
  }, [earliestDate, startDate]);

  const fetchTrucks = async (customer_id: string) => {
    const response = await callProtectedApi(
      `/trucks/list/${customer_id}`,
      'get',
      {},
      'Error occurred while fetching your trucks.'
    );
    return response?.data?.data?.trucks;
  };

  const { data: trucks, isLoading: loadingTrucks } = useQuery({
    queryKey: ['trucks', userId],
    queryFn: () => fetchTrucks(userId ?? ''),
    enabled: shouldFetchRealData,
    staleTime: 15 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });

  const fetchCostRevenuePerMileData = async (
    truck_id: string,
    start_date: string,
    end_date: string
  ) => {
    const response = await callProtectedApi(
      `/charts/cpm-rpm-chart/${userId ?? ''}/${truck_id}?start_date=${start_date}&end_date=${end_date}`,
      'get',
      {}
    );
    return {
      cpmRpmData: response?.data?.data?.cpmRpmData,
      totalMilesDriven: response?.data?.data?.totalMilesDriven,
    };
  };

  const {
    data: { cpmRpmData, totalMilesDriven } = {},
    isLoading: loadingCpmRpmData,
  } = useQuery<{
    cpmRpmData: CostRevenuePerMileData[];
    totalMilesDriven: number;
  }>({
    queryKey: ['cpmRpmData', selectedTruck, startDate, endDate, userId],
    queryFn: () =>
      fetchCostRevenuePerMileData(
        selectedTruck,
        startDate?.toISOString()?.split('T')[0] ?? '',
        endDate?.toISOString()?.split('T')[0]
      ),
    enabled: !!selectedTruck && !!startDate && !!endDate && shouldFetchRealData,
    staleTime: 15 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });

  const fetchAverageRevenueExpensesData = async (
    truck_id: string,
    start_date: string,
    end_date: string
  ) => {
    const response = await callProtectedApi(
      `/charts/revenue-expenses-chart/${userId ?? ''}/${truck_id}?start_date=${start_date}&end_date=${end_date}`,
      'get',
      {}
    );
    return response?.data?.data?.revenueExpensesData;
  };

  const { data: revenueExpensesData, isLoading: loadingRevenueExpensesData } =
    useQuery<RevenueExpensesData[]>({
      queryKey: [
        'revenueExpensesData',
        selectedTruck,
        startDate,
        endDate,
        userId,
      ],
      queryFn: () =>
        fetchAverageRevenueExpensesData(
          selectedTruck,
          startDate?.toISOString()?.split('T')[0] ?? '',
          endDate?.toISOString()?.split('T')[0]
        ),
      enabled:
        !!selectedTruck && !!startDate && !!endDate && shouldFetchRealData,
      staleTime: 15 * 60 * 1000,
      gcTime: 15 * 60 * 1000,
    });

  const fetchTrucksRevenueExpenseData = async (
    truck_id: string,
    start_date: string,
    end_date: string
  ) => {
    const response = await callProtectedApi(
      `/charts/trucks-revenue-expenses/${userId ?? ''}/${truck_id}?start_date=${start_date}&end_date=${end_date}`,
      'get',
      {}
    );
    return response?.data?.data?.trucksRevenueExpenseData;
  };

  const {
    data: trucksRevenueExpenseData,
    isLoading: loadingTrucksRevenueExpenseData,
  } = useQuery<TruckRevenueExpenseData[]>({
    queryKey: [
      'trucksRevenueExpenseData',
      selectedTruck,
      startDate,
      endDate,
      userId,
    ],
    queryFn: () =>
      fetchTrucksRevenueExpenseData(
        selectedTruck,
        startDate?.toISOString()?.split('T')[0] ?? '',
        endDate?.toISOString()?.split('T')[0]
      ),
    enabled: !!selectedTruck && !!startDate && !!endDate && shouldFetchRealData,
    staleTime: 15 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });

  const fetchBudgetedVsTargetByCategory = async (
    category: string,
    start_date: string,
    end_date: string
  ) => {
    const response = await callProtectedApi(
      `/charts/budgeted-vs-target-chart/${userId ?? ''}/${category}?start_date=${start_date}&end_date=${end_date}`,
      'get',
      {}
    );
    return response?.data?.data?.budgetedVsTargetData;
  };

  const { data: budgetedVsTargetData, isLoading: loadingBudgetedVsTargetData } =
    useQuery<BudgetedVsTargetByCategoryData[]>({
      queryKey: [
        'budgetedVsTargetByCategory',
        selectedCategory,
        startDate,
        endDate,
        userId,
      ],
      queryFn: () =>
        fetchBudgetedVsTargetByCategory(
          selectedCategory,
          startDate?.toISOString()?.split('T')[0] ?? '',
          endDate?.toISOString()?.split('T')[0]
        ),
      enabled:
        !!selectedCategory && !!startDate && !!endDate && shouldFetchRealData,
      staleTime: 15 * 60 * 1000,
      gcTime: 15 * 60 * 1000,
    });

  // ============================================================================
  // Data Resolution: Use demo data for pre-active customers, real data otherwise
  // ============================================================================

  // Initialize dates for demo data
  useEffect(() => {
    if (isPreActive && !startDate) {
      const demoEarliestDate = getDemoEarliestDate();
      setStartDate(new Date(demoEarliestDate));
    }
  }, [isPreActive, startDate]);

  const displayTrucks = useMemo(() => {
    return isPreActive ? getDemoTrucks() : (trucks ?? []);
  }, [isPreActive, trucks]);

  const displayCpmRpmData = useMemo(() => {
    return isPreActive ? getDemoCpmRpmData() : (cpmRpmData ?? []);
  }, [isPreActive, cpmRpmData]);

  const displayTotalMilesDriven = useMemo(() => {
    return isPreActive ? getDemoTotalMilesDriven() : (totalMilesDriven ?? 0);
  }, [isPreActive, totalMilesDriven]);

  const displayRevenueExpensesData = useMemo(() => {
    return isPreActive
      ? getDemoRevenueExpensesData()
      : (revenueExpensesData ?? []);
  }, [isPreActive, revenueExpensesData]);

  const displayTrucksRevenueExpenseData = useMemo(() => {
    return isPreActive
      ? getDemoTrucksRevenueExpenseData()
      : (trucksRevenueExpenseData ?? []);
  }, [isPreActive, trucksRevenueExpenseData]);

  const displayBudgetedVsTargetData = useMemo(() => {
    return isPreActive
      ? getDemoBudgetedVsTargetData()
      : (budgetedVsTargetData ?? []);
  }, [isPreActive, budgetedVsTargetData]);

  // Loading states: Never show loading for demo data
  const displayLoadingTrucks = isPreActive ? false : loadingTrucks;
  const displayLoadingEarliestDate = isPreActive ? false : loadingEarliestDate;
  const displayLoadingCpmRpmData = isPreActive ? false : loadingCpmRpmData;
  const displayLoadingRevenueExpensesData = isPreActive
    ? false
    : loadingRevenueExpensesData;
  const displayLoadingTrucksRevenueExpenseData = isPreActive
    ? false
    : loadingTrucksRevenueExpenseData;
  const displayLoadingBudgetedVsTargetData = isPreActive
    ? false
    : loadingBudgetedVsTargetData;

  useEffect(() => {
    if (
      userRecord &&
      userRecord?.userType === Entity.Customer &&
      userRecord?.customer_id?.length > 0 &&
      !isPreActive
    ) {
      fetchTrucks(userRecord?.customer_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRecord, isPreActive]);

  const handleSetSelectedTruck = (truck_id: string) => {
    setSelectedTruck(truck_id);
  };

  const selectedUnitNumber = useMemo(() => {
    if (selectedTruck === 'all') {
      return 'All Trucks';
    }
    return displayTrucks.find(
      (truck: Truck) => truck.truck_id === selectedTruck
    )?.truck_display_name;
  }, [displayTrucks, selectedTruck]);

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
        return (
          <>
            {/* <OnboardingModal /> */}
            {!displayLoadingTrucks && !displayLoadingEarliestDate ? (
              <div className="flex h-full w-[1148px] max-w-[1148px] flex-col space-y-[16px] overflow-y-auto overflow-x-hidden pt-[16px]">
                {isPreActive && <DemoDataAlert />}
                <div className="flex w-full flex-row items-center justify-center align-middle">
                  <ChartFiltersWidget
                    trucks={displayTrucks}
                    setSelectedTruck={handleSetSelectedTruck}
                    selectedTruck={selectedTruck}
                    loadingTrucks={displayLoadingTrucks}
                    startDate={startDate}
                    endDate={endDate}
                    setStartDate={setStartDate}
                    setEndDate={setEndDate}
                    isPreActive={isPreActive}
                  />
                </div>
                <div className="flex w-full flex-row items-center justify-center align-middle">
                  <CostRevenuePerMileWidget
                    cpmRpmData={displayCpmRpmData}
                    milesDriven={displayTotalMilesDriven}
                    loadingCpmRpmData={displayLoadingCpmRpmData}
                    selectedUnitNumber={selectedUnitNumber}
                    isPreActive={isPreActive}
                  />
                </div>
                <div className="flex w-full flex-row items-center justify-center align-middle">
                  <RevenueAndExpensesWidget
                    revenueExpensesData={displayRevenueExpensesData}
                    loadingRevenueExpensesData={
                      displayLoadingRevenueExpensesData
                    }
                    showHistorical={true}
                    selectedUnitNumber={selectedUnitNumber}
                    isPreActive={isPreActive}
                  />
                </div>
                <div className="flex h-[600px] max-h-[600px] w-full flex-row items-start justify-start gap-x-[16px] align-middle">
                  <RevenuevsExpensesStackedBars
                    addClass="w-full h-[600px]"
                    trucksData={displayTrucksRevenueExpenseData}
                    loading={displayLoadingTrucksRevenueExpenseData}
                    isPreActive={isPreActive}
                  />
                  <BudgetVsActualExpensesWidget
                    budgetedVsTargetData={displayBudgetedVsTargetData}
                    loadingBudgetedVsTargetData={
                      displayLoadingBudgetedVsTargetData
                    }
                    selectedCategory={selectedCategory}
                    setSelectedCategory={setSelectedCategory}
                    addClass="w-full h-[600px]"
                    selectedUnitNumber={selectedUnitNumber}
                    isPreActive={isPreActive}
                  />
                </div>
              </div>
            ) : (
              <ClipLoader
                color="#1D213B"
                loading={displayLoadingTrucks || displayLoadingEarliestDate}
                size={60}
                aria-label="Loading Spinner"
                data-testid="loader"
                className="mt-10 flex h-full w-full items-center justify-center"
              />
            )}
          </>
        );
      case 'tablet':
        return (
          <>
            {/* <OnboardingModal /> */}
            {!displayLoadingTrucks && !displayLoadingEarliestDate ? (
              <div className="flex h-full w-full flex-col space-y-[16px] overflow-y-auto overflow-x-hidden px-4 pb-4">
                {isPreActive && <DemoDataAlert />}
                <div className="flex w-full flex-row items-center justify-center align-middle">
                  <ChartFiltersWidget
                    trucks={displayTrucks}
                    setSelectedTruck={handleSetSelectedTruck}
                    selectedTruck={selectedTruck}
                    loadingTrucks={displayLoadingTrucks}
                    startDate={startDate}
                    endDate={endDate}
                    setStartDate={setStartDate}
                    setEndDate={setEndDate}
                    isPreActive={isPreActive}
                  />
                </div>
                <div className="flex w-full flex-row items-center justify-center align-middle">
                  <CostRevenuePerMileWidget
                    cpmRpmData={displayCpmRpmData}
                    milesDriven={displayTotalMilesDriven}
                    loadingCpmRpmData={displayLoadingCpmRpmData}
                    selectedUnitNumber={selectedUnitNumber}
                    isPreActive={isPreActive}
                  />
                </div>
                <div className="flex w-full flex-row items-center justify-center align-middle">
                  <RevenueAndExpensesWidget
                    revenueExpensesData={displayRevenueExpensesData}
                    loadingRevenueExpensesData={
                      displayLoadingRevenueExpensesData
                    }
                    showHistorical={true}
                    selectedUnitNumber={selectedUnitNumber}
                    isPreActive={isPreActive}
                  />
                </div>
                <div className="flex max-h-[500px] w-full flex-col items-start justify-start gap-x-[16px] align-middle">
                  <BudgetVsActualExpensesWidget
                    budgetedVsTargetData={displayBudgetedVsTargetData}
                    loadingBudgetedVsTargetData={
                      displayLoadingBudgetedVsTargetData
                    }
                    selectedCategory={selectedCategory}
                    setSelectedCategory={setSelectedCategory}
                    addClass="w-full"
                    selectedUnitNumber={selectedUnitNumber}
                    isPreActive={isPreActive}
                  />
                </div>
                <div className="flex w-full flex-row items-start justify-start gap-x-[16px] align-middle">
                  <RevenuevsExpensesStackedBars
                    addClass="w-full"
                    trucksData={displayTrucksRevenueExpenseData}
                    loading={displayLoadingTrucksRevenueExpenseData}
                    isPreActive={isPreActive}
                  />
                </div>
              </div>
            ) : (
              <ClipLoader
                color="#1D213B"
                loading={displayLoadingTrucks || displayLoadingEarliestDate}
                size={60}
                aria-label="Loading Spinner"
                data-testid="loader"
                className="mt-10 flex h-full w-full items-center justify-center"
              />
            )}
          </>
        );
      case 'mobile':
        return (
          <>
            {/* <OnboardingModal /> */}
            {!displayLoadingTrucks && !displayLoadingEarliestDate ? (
              <div className="flex h-full w-full flex-col space-y-[16px] overflow-y-auto overflow-x-hidden px-4 pb-4">
                {isPreActive && <DemoDataAlert />}
                <div className="flex w-full flex-row items-center justify-center align-middle">
                  <ChartFiltersWidget
                    trucks={displayTrucks}
                    setSelectedTruck={handleSetSelectedTruck}
                    selectedTruck={selectedTruck}
                    loadingTrucks={displayLoadingTrucks}
                    startDate={startDate}
                    endDate={endDate}
                    setStartDate={setStartDate}
                    setEndDate={setEndDate}
                    isPreActive={isPreActive}
                  />
                </div>
                <div className="flex w-full flex-row items-center justify-center align-middle">
                  <CostRevenuePerMileWidget
                    cpmRpmData={displayCpmRpmData}
                    milesDriven={displayTotalMilesDriven}
                    loadingCpmRpmData={displayLoadingCpmRpmData}
                    selectedUnitNumber={selectedUnitNumber}
                    isPreActive={isPreActive}
                  />
                </div>
                <div className="flex w-full flex-row items-center justify-center align-middle">
                  <RevenueAndExpensesWidget
                    revenueExpensesData={displayRevenueExpensesData}
                    loadingRevenueExpensesData={
                      displayLoadingRevenueExpensesData
                    }
                    showHistorical={true}
                    selectedUnitNumber={selectedUnitNumber}
                    isPreActive={isPreActive}
                  />
                </div>
                <div className="flex max-h-[500px] w-full flex-col items-start justify-start gap-x-[16px] align-middle">
                  <BudgetVsActualExpensesWidget
                    budgetedVsTargetData={displayBudgetedVsTargetData}
                    loadingBudgetedVsTargetData={
                      displayLoadingBudgetedVsTargetData
                    }
                    selectedCategory={selectedCategory}
                    setSelectedCategory={setSelectedCategory}
                    addClass="w-full"
                    selectedUnitNumber={selectedUnitNumber}
                    isPreActive={isPreActive}
                  />
                </div>
                <div className="flex w-full flex-row items-start justify-start gap-x-[16px] align-middle">
                  <RevenuevsExpensesStackedBars
                    addClass="w-full"
                    trucksData={displayTrucksRevenueExpenseData}
                    loading={displayLoadingTrucksRevenueExpenseData}
                    isPreActive={isPreActive}
                  />
                </div>
              </div>
            ) : (
              <ClipLoader
                color="#1D213B"
                loading={displayLoadingTrucks || displayLoadingEarliestDate}
                size={60}
                aria-label="Loading Spinner"
                data-testid="loader"
                className="mt-10 flex h-full w-full items-center justify-center"
              />
            )}
          </>
        );
    }
  };

  return <>{renderScreenView(screenView)}</>;
}

export default FinancialOverview;
